/**
 * threadTitleAuthority — who gets to name a shared thread.
 *
 * Reported 2026-08-05: Fernando and Malaika sat in the same shared thread and
 * saw two different names for it ("Chat Response Test" vs "Hello Test
 * Exchange"). Nothing was corrupt — the LLM thread labeler runs INDEPENDENTLY
 * in each participant's browser, writes straight to that browser's local thread
 * record, and never publishes to the canonical title on the membership doc. Two
 * clients, two inferences, two names for one conversation.
 *
 * The server already has the correct machinery: a canonical title saved on the
 * membership doc propagates into every participant's thread index — but ONLY
 * when the thread's creator publishes it (`propagateTitleToParticipantIndexes`
 * is gated on `after.createdBy === creatorId`). So the rule this module encodes
 * is the one the server already enforces:
 *
 *   • the CREATOR is the naming authority — their auto-inferred title is
 *     published as canonical and fans out to everyone;
 *   • a PARTICIPANT adopts the canonical and never lets its local labeler
 *     overwrite it — otherwise each side clobbers what the server just
 *     propagated and the two names oscillate forever;
 *   • a SOLO thread is unaffected: local labeling, no publishing.
 *
 * An explicit user rename is unchanged and still wins locally (`userRenamed`);
 * this governs only the automatic labeler.
 *
 * Pure + dependency-free so the rule is unit-tested without React or Firestore.
 */

/** Minimal shape read from a membership row. */
export interface SharedThreadOwnership {
  /** uid that created the thread — the naming authority. */
  createdBy?: string | null;
}

export type ThreadTitleAuthority =
  /** Not a shared thread — label locally, publish nothing. */
  | 'solo'
  /** Shared and self created it — self names it for everyone. */
  | 'owner'
  /** Shared, someone else created it — adopt the canonical, never overwrite. */
  | 'participant';

function trim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Decide this user's authority over a thread's title.
 *
 * Fail-safe toward 'participant' for a shared thread with unknown ownership:
 * publishing a title we have no claim to is the harmful direction (it would
 * rename the thread under everyone else), whereas declining to publish merely
 * leaves the name as it is.
 */
export function resolveThreadTitleAuthority(args: {
  threadId: string | null | undefined;
  selfId: string | null | undefined;
  /** threadId → ownership, from the membership snapshot. Absent ⇒ not shared. */
  sharedThreads?: Readonly<Record<string, SharedThreadOwnership>> | null;
}): ThreadTitleAuthority {
  const threadId = trim(args.threadId);
  const selfId = trim(args.selfId);
  if (!threadId) return 'solo';

  const shared = args.sharedThreads ?? {};
  if (!Object.prototype.hasOwnProperty.call(shared, threadId)) return 'solo';

  const createdBy = trim(shared[threadId]?.createdBy);
  if (!selfId || !createdBy) return 'participant';
  return createdBy === selfId ? 'owner' : 'participant';
}

/**
 * Should the local automatic labeler be prevented from rewriting this thread's
 * title? True only for a shared thread this user did not create — their name
 * for it is not theirs to choose, and letting the labeler write would fight the
 * canonical title the server propagates.
 */
export function isAutoTitleSuppressed(authority: ThreadTitleAuthority): boolean {
  return authority === 'participant';
}

/**
 * Should a locally-inferred title be published as the canonical title for all
 * participants? Only the creator of a shared thread, and only for a title worth
 * propagating (a blank or default one is never published — the fallbacks are
 * per-client presentation, not a name anyone chose).
 */
export function shouldPublishCanonicalTitle(
  authority: ThreadTitleAuthority,
  title: string | null | undefined,
): boolean {
  if (authority !== 'owner') return false;
  return trim(title).length > 0;
}
